import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { PlusIcon, XMarkIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/ui/number-input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DatePicker } from '../components/ui/date-picker';
import ProviderCombobox from '../components/ProviderCombobox';
import ProductCombobox from '../components/ProductCombobox';
import { hoyLocal } from '../utils/fechaLocal';
import { NC_KINDS } from '../lib/purchaseCreditNoteKinds';

const fmtARS = (n: number) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 2 }).format(Number(n) || 0);

interface Row {
  key: string;
  productId?: string; supplyId?: string; itemType: 'product' | 'supply' | 'manual';
  name: string; qty: number; unitCost: number; taxRate: number;
  fromOC: boolean;      // vino de la orden de compra
  selected: boolean;    // tildado para incluir en la NC
  maxQty?: number;      // cantidad recibida (tope para devolución parcial)
}

const lineTotal = (r: { qty: number; unitCost: number; taxRate: number }) =>
  (Number(r.qty) || 0) * (Number(r.unitCost) || 0) * (1 + (Number(r.taxRate) || 0) / 100);

const CreatePurchaseCreditNote: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [providers, setProviders] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [supplies, setSupplies] = useState<any[]>([]);
  const [purchases, setPurchases] = useState<any[]>([]);

  const [providerId, setProviderId] = useState('');
  const [purchaseId, setPurchaseId] = useState('');
  const [providerNc, setProviderNc] = useState('');
  const [kind, setKind] = useState('');
  const [affectsStock, setAffectsStock] = useState(true);
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(hoyLocal());
  const [rows, setRows] = useState<Row[]>([]);
  const [searchType, setSearchType] = useState<'product' | 'supply'>('product');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [saving, setSaving] = useState(false);
  // Herramientas/equipos que se devolvieron físicamente → se dan de baja al guardar
  const [assets, setAssets] = useState<any[]>([]);
  const [returnedAssetIds, setReturnedAssetIds] = useState<string[]>([]);
  const [assetQuery, setAssetQuery] = useState('');

  useEffect(() => {
    axios.get('/api/providers?limit=1000').then(r => setProviders(r.data.providers || [])).catch(() => {});
    axios.get('/api/products?limit=all').then(r => setProducts(r.data.products || [])).catch(() => {});
    axios.get('/api/supplies?limit=1000').then(r => setSupplies(r.data.supplies || [])).catch(() => {});
    axios.get('/api/purchases').then(r => setPurchases(r.data.purchases || [])).catch(() => {});
    // Solo herramientas/equipos operativos (para dar de baja si se devolvieron)
    axios.get('/api/assets?estado=operativo').then(r => setAssets(r.data.assets || [])).catch(() => {});
  }, []);

  // ?purchaseId= desde una compra: precargar proveedor + OC + ítems
  useEffect(() => {
    const pu = searchParams.get('purchaseId');
    if (pu && purchases.length) { setKind('devolucion'); selectPurchase(pu, true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchases.length]);

  const providerPurchases = useMemo(
    () => purchases.filter(p => !providerId || p.providerId === providerId),
    [purchases, providerId]
  );

  const ocRowsFromPurchase = (p: any): Row[] =>
    (p?.items || []).map((it: any, idx: number) => {
      const maxQty = Number(it.receivedQty || it.qty || 0);
      return {
        key: `oc-${idx}`,
        productId: it.productId || undefined,
        supplyId: it.supplyId || undefined,
        itemType: (it.itemType || (it.supplyId ? 'supply' : it.productId ? 'product' : 'manual')) as Row['itemType'],
        name: it.name || '',
        qty: maxQty,
        unitCost: Number(it.unitCost || 0),
        taxRate: Number(it.taxRate || 0),
        fromOC: true,
        selected: true,   // por defecto: toda la compra
        maxQty,
      };
    });

  // withItems: en una devolución la OC trae sus ítems; en los otros tipos es solo referencia
  const selectPurchase = (id: string, withItems: boolean) => {
    setPurchaseId(id);
    const p = purchases.find(x => x.id === id);
    if (!p) { setRows(prev => prev.filter(r => !r.fromOC)); return; }
    if (p.providerId) setProviderId(p.providerId);
    if (!withItems) return;
    // reemplazar los ítems de OC, conservar los sueltos que hayan agregado
    setRows(prev => [...ocRowsFromPurchase(p), ...prev.filter(r => !r.fromOC)]);
    setAdjustAmount('');
  };

  const isReturn = kind === 'devolucion';
  const changeKind = (k: string) => {
    setKind(k);
    if (k === 'devolucion') {
      setAffectsStock(true); setAdjustAmount('');
      if (purchaseId) selectPurchase(purchaseId, true);
    } else {
      setRows([]); setAffectsStock(false); setReturnedAssetIds([]);
    }
  };
  const reasonPlaceholder = ({
    devolucion: 'Rotura, producto equivocado, no era lo pedido...',
    pronto_pago: 'Ej. 5% por pago dentro de los 7 días',
    bonificacion: 'Bonificación de fin de mes, objetivos, acuerdo comercial...',
    error_facturacion: 'Diferencia de precio, ítem facturado de más...',
  } as Record<string, string>)[kind] || '';

  const addFromCatalog = (row: { id: string; name: string }, type: 'product' | 'supply') => {
    const src = type === 'product' ? products.find((p: any) => p.id === row.id) : supplies.find((s: any) => s.id === row.id);
    setRows(prev => [...prev, {
      key: `x-${prev.length}-${row.id}`,
      productId: type === 'product' ? row.id : undefined,
      supplyId: type === 'supply' ? row.id : undefined,
      itemType: type,
      name: src?.name || row.name,
      qty: 1, unitCost: Number(src?.costPrice || 0), taxRate: 21,
      fromOC: false, selected: true,
    }]);
  };

  const patch = (key: string, p: Partial<Row>) => setRows(prev => prev.map(r => (r.key === key ? { ...r, ...p } : r)));
  const remove = (key: string) => setRows(prev => prev.filter(r => r.key !== key));
  const setAllOC = (sel: boolean) => setRows(prev => prev.map(r => (r.fromOC ? { ...r, selected: sel } : r)));

  const ocRows = rows.filter(r => r.fromOC);
  const extraRows = rows.filter(r => !r.fromOC);
  const includedRows = rows.filter(r => r.selected && Number(r.qty) > 0);
  const itemsTotal = includedRows.reduce((s, r) => s + lineTotal(r), 0);
  const totalNC = isReturn ? itemsTotal : (parseFloat(adjustAmount) || 0);

  const save = async () => {
    if (!providerId) { toast.error('Elegí el proveedor'); return; }
    if (!kind) { toast.error('Elegí el tipo de nota de crédito'); return; }
    if (isReturn && includedRows.length === 0) { toast.error('Tildá al menos un ítem devuelto'); return; }
    if (!isReturn && !(parseFloat(adjustAmount) > 0)) { toast.error('Cargá el monto de la nota de crédito'); return; }
    setSaving(true);
    try {
      await axios.post('/api/purchase-credit-notes', {
        providerId,
        purchaseId: purchaseId || undefined,
        providerNcNumber: providerNc || undefined,
        kind,
        affectsStock: isReturn && includedRows.length > 0 ? affectsStock : false,
        reason: reason || undefined,
        date,
        items: (isReturn ? includedRows : []).map(r => ({
          productId: r.productId, supplyId: r.supplyId, itemType: r.itemType,
          name: r.name, qty: Number(r.qty), unitCost: Number(r.unitCost), taxRate: Number(r.taxRate),
        })),
        total: isReturn ? undefined : parseFloat(adjustAmount) || 0,
        returnedAssetIds: isReturn ? returnedAssetIds : [],
      });
      toast.success('Nota de crédito registrada');
      navigate('/purchases/credit-notes');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'No se pudo registrar la nota de crédito');
    } finally { setSaving(false); }
  };

  const ItemsTable = ({ list, title, extra }: { list: Row[]; title: string; extra?: React.ReactNode }) => (
    <div className="cq-table rounded-lg border">
      <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/20">
        <span className="text-sm font-medium">{title}</span>
        {extra}
      </div>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent border-b">
            <TableHead className="h-8 w-8 px-2" />
            <TableHead className="h-8 px-2 text-xs">Ítem</TableHead>
            <TableHead className="h-8 px-2 text-xs text-center">Cant.</TableHead>
            <TableHead className="h-8 px-2 text-xs text-right">Costo neto</TableHead>
            <TableHead className="h-8 px-2 text-xs text-center cq-hide-narrow">IVA</TableHead>
            <TableHead className="h-8 px-2 text-xs text-right">Total</TableHead>
            <TableHead className="h-8 w-8" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.map(r => (
            <TableRow key={r.key} className={`border-b ${r.selected ? '' : 'opacity-50'}`}>
              <TableCell className="px-2 py-1.5 text-center">
                <input type="checkbox" checked={r.selected} onChange={e => patch(r.key, { selected: e.target.checked })} className="h-4 w-4" />
              </TableCell>
              <TableCell className="px-2 py-1.5 text-sm">
                {r.name}
                {r.fromOC && r.maxQty ? <span className="ml-1 text-xs text-muted-foreground">(recibido: {r.maxQty})</span> : null}
              </TableCell>
              <TableCell className="px-2 py-1.5">
                <NumberInput min={0} max={r.maxQty} value={r.qty}
                  onChange={(v) => patch(r.key, { qty: v })}
                  disabled={!r.selected} className="h-8 w-16 text-center mx-auto" />
              </TableCell>
              <TableCell className="px-2 py-1.5">
                <NumberInput min={0} value={r.unitCost}
                  onChange={(v) => patch(r.key, { unitCost: v })}
                  disabled={!r.selected} className="h-8 w-24 text-right ml-auto" />
              </TableCell>
              <TableCell className="px-2 py-1.5 text-center cq-hide-narrow">
                <Select value={String(r.taxRate)} onValueChange={v => patch(r.key, { taxRate: Number(v) })} disabled={!r.selected}>
                  <SelectTrigger className="h-8 w-20 mx-auto"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0%</SelectItem>
                    <SelectItem value="10.5">10.5%</SelectItem>
                    <SelectItem value="21">21%</SelectItem>
                    <SelectItem value="27">27%</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell className="px-2 py-1.5 text-right text-sm font-medium whitespace-nowrap">{fmtARS(lineTotal(r))}</TableCell>
              <TableCell className="px-2 py-1.5 text-right">
                {!r.fromOC && <button type="button" onClick={() => remove(r.key)} className="text-muted-foreground hover:text-red-500"><XMarkIcon className="h-4 w-4" /></button>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="space-y-5 p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={() => navigate('/purchases/credit-notes')} className="shrink-0"><ArrowLeftIcon className="h-4 w-4" /></Button>
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Nueva nota de crédito de compra</h2>
          <p className="text-sm text-muted-foreground">Devolución, bonificación o ajuste del proveedor. Baja la deuda en su cuenta corriente.</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-5 space-y-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-sm">Proveedor *</Label>
              <ProviderCombobox providers={providers}
                selectedProvider={providers.find((p: any) => p.id === providerId) || null}
                onSelect={(p: any) => { setProviderId(p?.id || ''); setPurchaseId(''); setRows(prev => prev.filter(r => !r.fromOC)); }} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Tipo de nota de crédito *</Label>
              <Select value={kind} onValueChange={changeKind}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Elegí el tipo..." /></SelectTrigger>
                <SelectContent>
                  {NC_KINDS.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Fecha</Label>
              <DatePicker value={date} onChange={setDate} className="h-9 w-full" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">N° de NC del proveedor (opcional)</Label>
              <Input value={providerNc} onChange={e => setProviderNc(e.target.value)} placeholder="Ej. 0001-00001234" className="h-9" />
            </div>
          </div>

          {!kind && (
            <div className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
              Elegí el tipo de nota de crédito para continuar.
            </div>
          )}

          {isReturn && (
            <div className="space-y-4">
              <div className="space-y-1.5 sm:max-w-md">
                <Label className="text-sm">Orden de compra (opcional)</Label>
                <Select value={purchaseId || 'none'} onValueChange={v => (v === 'none' ? (setPurchaseId(''), setRows(prev => prev.filter(r => !r.fromOC))) : selectPurchase(v, true))}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Sin OC de referencia" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin OC de referencia</SelectItem>
                    {providerPurchases.map(p => <SelectItem key={p.id} value={p.id}>{p.purchaseNumber} — {fmtARS(p.total)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Si la elegís, trae sus ítems al costo de compra para tildar los que devolvés.</p>
              </div>

              {ocRows.length > 0 && (
                <ItemsTable
                  list={ocRows}
                  title={`Ítems de ${purchases.find(p => p.id === purchaseId)?.purchaseNumber || 'la compra'} — tildá los que devolvés`}
                  extra={
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => setAllOC(true)} className="text-xs underline text-muted-foreground hover:text-foreground">Toda la compra</button>
                      <button type="button" onClick={() => setAllOC(false)} className="text-xs underline text-muted-foreground hover:text-foreground">Ninguno</button>
                    </div>
                  }
                />
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">{ocRows.length > 0 ? 'Agregar otro ítem devuelto' : 'Ítems devueltos'}</Label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <input type="checkbox" checked={affectsStock} onChange={e => setAffectsStock(e.target.checked)} className="h-4 w-4" disabled={includedRows.length === 0} />
                    Descontar del stock
                  </label>
                </div>
                <div className="flex gap-2">
                  <Select value={searchType} onValueChange={v => setSearchType(v as 'product' | 'supply')}>
                    <SelectTrigger className="h-9 w-32 shrink-0"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="product">Producto</SelectItem>
                      <SelectItem value="supply">Insumo</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex-1">
                    <ProductCombobox
                      products={(searchType === 'product' ? products : supplies).map((p: any) => ({
                        id: p.id, name: p.name, sellingPrice: Number(p.costPrice || 0),
                        category: searchType === 'product' ? (p.category ?? '') : 'insumo', isService: false, sku: p.sku || '', stock: p.stock,
                      }))}
                      selectedProduct={null}
                      onSelect={(row: any) => addFromCatalog(row, searchType)}
                      placeholder={searchType === 'product' ? 'Buscar producto...' : 'Buscar insumo...'}
                    />
                  </div>
                </div>
                {extraRows.length > 0 && <ItemsTable list={extraRows} title="Ítems sueltos" />}
                {includedRows.length === 0 && (
                  <p className="text-xs text-muted-foreground">Elegí una orden de compra o buscá los productos que devolviste.</p>
                )}
              </div>

              {/* Equipos/herramientas devueltos → dar de baja la ficha. Solo si el
                  equipo VOLVIO fisicamente. Si te reintegran plata pero lo seguis
                  usando, no tildes nada. */}
              {assets.length > 0 && (
                <div className="rounded-lg border p-3 space-y-2">
                  <Label className="text-sm font-medium">¿Se devolvió alguna herramienta o equipo?</Label>
                  <p className="text-xs text-muted-foreground">
                    Tildá solo lo que volvió físicamente al proveedor: esas fichas se dan de baja de Equipos y herramientas.
                  </p>
                  <Input value={assetQuery} onChange={e => setAssetQuery(e.target.value)} placeholder="Buscar por código o nombre..." className="h-9 max-w-sm" />
                  <div className="max-h-44 overflow-y-auto rounded-md border divide-y">
                    {assets
                      .filter(a => {
                        const q = assetQuery.trim().toLowerCase();
                        if (!q) return returnedAssetIds.includes(a.id); // sin búsqueda, mostrar solo los tildados
                        return `${a.code} ${a.description} ${a.marca || ''}`.toLowerCase().includes(q);
                      })
                      .slice(0, 30)
                      .map(a => (
                        <label key={a.id} className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-sm hover:bg-muted/40">
                          <input type="checkbox" className="h-4 w-4"
                            checked={returnedAssetIds.includes(a.id)}
                            onChange={() => setReturnedAssetIds(prev => prev.includes(a.id) ? prev.filter(x => x !== a.id) : [...prev, a.id])} />
                          <span className="font-mono text-xs text-muted-foreground">{a.code}</span>
                          <span>{a.description}</span>
                          {a.marca ? <span className="text-xs text-muted-foreground">· {a.marca}</span> : null}
                        </label>
                      ))}
                    {assetQuery.trim() === '' && returnedAssetIds.length === 0 && (
                      <div className="px-2 py-2 text-xs text-muted-foreground">Escribí arriba para buscar el equipo devuelto.</div>
                    )}
                  </div>
                  {returnedAssetIds.length > 0 && (
                    <p className="text-xs text-amber-600">Se darán de baja {returnedAssetIds.length} ficha(s) al guardar.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {kind && !isReturn && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-sm">Monto de la nota de crédito * <span className="font-normal text-muted-foreground">(IVA incluido)</span></Label>
                <Input type="number" min={0} value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)} placeholder="0" className="h-9" />
                <p className="text-xs text-muted-foreground">No toca stock: suma crédito a favor en la cuenta corriente del proveedor.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Compra a la que corresponde (opcional)</Label>
                <Select value={purchaseId || 'none'} onValueChange={v => (v === 'none' ? setPurchaseId('') : selectPurchase(v, false))}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Sin OC de referencia" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin OC de referencia</SelectItem>
                    {providerPurchases.map(p => <SelectItem key={p.id} value={p.id}>{p.purchaseNumber} — {fmtARS(p.total)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {kind && (
            <>
              <div className="space-y-1.5">
                <Label className="text-sm">Detalle (opcional)</Label>
                <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder={reasonPlaceholder} rows={2} />
              </div>

              <div className="flex items-center justify-between rounded-lg bg-muted/40 px-4 py-3">
                <span className="text-sm font-medium">Total de la nota de crédito</span>
                <span className="text-xl font-bold">{fmtARS(totalNC)}</span>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => navigate('/purchases/credit-notes')} disabled={saving}>Cancelar</Button>
                <Button onClick={save} disabled={saving}>{saving ? 'Guardando...' : 'Registrar nota de crédito'}</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CreatePurchaseCreditNote;
